/**
 * SourceLanguageSection
 *
 * Single-language selector with an "Auto-detect" option (value = "auto").
 * Reuses NLLBLanguageSelector with maxSelection=1 and showAutoDetect=true.
 */

import { memo, useCallback } from 'react';
import { Mic } from 'lucide-react';
import { NLLBLanguageSelector } from '@/components/common/LanguageSelector';
import { useTranslation } from '@/hooks/useTranslation';
import { useSubtitles } from '../SubtitlesContext.context';

export const SourceLanguageSection = memo(function SourceLanguageSection() {
  const { t } = useTranslation();
  const { state, isUploading, isGenerating, handleSourceLanguageChange } = useSubtitles();
  const { sourceLanguage } = state;

  // NLLBLanguageSelector works with string[] so we wrap/unwrap
  const values = sourceLanguage ? [sourceLanguage] : ['auto'];

  const handleChange = useCallback(
    (langs: string[]) => {
      const lang = langs[0] ?? 'auto';
      handleSourceLanguageChange(lang);
    },
    [handleSourceLanguageChange]
  );

  return (
    <div className="panel-glass">
      <h3 className="section-heading">
        <Mic className="h-4 w-4 sm:h-5 sm:w-5 text-purple-400" />
        <span>{t('subtitles.sourceLanguage.title', { defaultValue: 'Source Language' })}</span>
      </h3>
      <p className="text-sm text-white/60 mb-3">
        {t('subtitles.sourceLanguage.description', {
          defaultValue:
            'The language spoken in your media. Select "Auto-detect" to let AI identify it.',
        })}
      </p>
      <NLLBLanguageSelector
        values={values}
        onMultiChange={handleChange}
        maxSelection={1}
        showQuickActions={false}
        showAutoDetect={true}
        disabled={isUploading || isGenerating}
        placeholder={t('subtitles.sourceLanguage.placeholder', {
          defaultValue: 'Select source language...',
        })}
      />
    </div>
  );
});
